import { Link } from 'react-router'
import { ChartCard } from '@/components/patterns/ChartCard'
import { tagVar } from '@/components/ui/tag-colors'
import { CrmIcon } from '../components/CrmIcon'
import { categoryHref } from './links'
import { plural } from './text'
import type { CategoryCountRow, Scope } from './types'

/** Widget 5: relations per category as horizontal bars in the category's colour, two links per row (design §4.1). */
export function CategoryWidget({ rows, scope }: { rows: CategoryCountRow[]; scope: Scope }) {
  const max = Math.max(1, ...rows.map((row) => row.persons + row.organisations))
  return (
    <ChartCard
      title="Relaties per categorie"
      definition="Personen en organisaties; klik een aantal voor de gefilterde lijst"
      empty={rows.length === 0 ? { title: 'Nog geen relaties met een categorie.' } : null}
    >
      <ul className="flex flex-col gap-2">
        {rows.map((row) => {
          const total = row.persons + row.organisations
          const linkable = row.key !== 'other'
          const categoryId = row.key === 'none' ? null : row.category_id
          const color = row.key === 'category' ? tagVar(row.color, 'deep') : 'var(--muted-foreground)'
          return (
            <li key={row.category_id ?? row.key} className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="flex min-w-0 items-center gap-1.5 font-medium">
                  {row.key === 'category' ? <CrmIcon name={row.icon} className="size-4 shrink-0" /> : null}
                  <span className="truncate" style={row.key === 'category' ? { color } : undefined}>
                    {row.name}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2 tabular-nums text-muted-foreground">
                  {linkable ? (
                    <>
                      <Link to={categoryHref('contacten', categoryId, scope)} className="underline-offset-2 hover:underline">
                        {plural(row.persons, 'persoon', 'personen')}
                      </Link>
                      <Link to={categoryHref('organisaties', categoryId, scope)} className="underline-offset-2 hover:underline">
                        {plural(row.organisations, 'organisatie', 'organisaties')}
                      </Link>
                    </>
                  ) : (
                    <span>
                      {plural(row.persons, 'persoon', 'personen')} · {plural(row.organisations, 'organisatie', 'organisaties')}
                    </span>
                  )}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-muted" aria-hidden>
                <div className="h-2 rounded-full" style={{ width: `${(total / max) * 100}%`, backgroundColor: color }} />
              </div>
            </li>
          )
        })}
      </ul>
    </ChartCard>
  )
}
